import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { testimonials } from '@/mocks/data';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setSubmitting(true);
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9rd8a4nlsngrm4lmahg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于禁漫天堂',
    description: '禁漫天堂品牌介绍、用户口碑评价与联系方式',
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700 mb-4">
                关于我们
              </span>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading mb-4">
                禁漫天堂 - 您的一站式漫画阅读平台
              </h1>
              <p className="text-sm text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                禁漫天堂致力于为广大漫画爱好者提供最优质的在线阅读体验，汇聚海量漫画资源，
                打造便捷、快速、安全的漫画阅读社区
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-14">
              <div className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-eye-line text-xl"></i>
                </div>
                <div className="text-2xl font-bold text-foreground-900 font-heading mb-1">500万+</div>
                <p className="text-xs text-foreground-500">累计注册用户</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-book-line text-xl"></i>
                </div>
                <div className="text-2xl font-bold text-foreground-900 font-heading mb-1">3万+</div>
                <p className="text-xs text-foreground-500">漫画作品收录</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                  <i className="ri-star-line text-xl"></i>
                </div>
                <div className="text-2xl font-bold text-foreground-900 font-heading mb-1">98%</div>
                <p className="text-xs text-foreground-500">用户满意度</p>
              </div>
            </div>

            <div className="prose-custom text-sm text-foreground-700 leading-relaxed space-y-4">
              <p>
                禁漫天堂成立于2020年，经过多年的发展与打磨，已经成为国内领先的漫画在线阅读平台之一。
                我们始终坚持以用户为中心的产品理念，不断优化阅读体验，丰富内容资源，
                致力于为每一位漫友提供最好的漫画阅读服务。
              </p>
              <p>
                平台采用先进的云架构技术，在全球范围内部署了多个加速节点，
                确保用户无论身处何地都能获得流畅快速的阅读体验。
                同时，我们拥有专业的内容审核和推荐团队，
                严格保障平台内容质量，为用户筛选最优质的漫画作品。
              </p>
              <p>
                未来，禁漫天堂将继续深耕漫画阅读领域，
                探索更多创新功能和服务模式，与广大漫友一起成长，共同打造更加美好的漫画阅读生态。
              </p>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500">来自真实用户的使用评价</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">{t.content}</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-xs font-bold">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.date}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500">
                有任何问题或建议？请填写以下表单，我们会尽快回复您
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-5"
            >
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  您的姓名 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-md bg-background-100 border border-background-200/70 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 text-foreground-900 placeholder:text-foreground-400"
                  placeholder="请输入您的姓名"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  电子邮箱 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-md bg-background-100 border border-background-200/70 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 text-foreground-900 placeholder:text-foreground-400"
                  placeholder="请输入您的邮箱地址"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-2.5 text-sm rounded-md bg-background-100 border border-background-200/70 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 text-foreground-900 placeholder:text-foreground-400"
                  placeholder="请选择或输入咨询主题"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 text-sm rounded-md bg-background-100 border border-background-200/70 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 text-foreground-900 placeholder:text-foreground-400 resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              {/* Honeypot */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="absolute left-[-9999px] opacity-0 h-0 w-0 overflow-hidden pointer-events-none"
              />

              <button
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto px-8 py-2.5 text-sm font-medium whitespace-nowrap rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? '提交中...' : '提交留言'}
              </button>

              {formStatus === 'success' && (
                <div className="p-4 rounded-md bg-accent-100 border border-accent-200 text-sm text-accent-800">
                  感谢您的留言！我们会尽快回复您。
                </div>
              )}
              {formStatus === 'error' && formError && (
                <div className="p-4 rounded-md bg-primary-100 border border-primary-200 text-sm text-primary-800">
                  {formError}
                </div>
              )}
            </form>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <p className="text-xs font-medium text-foreground-900 mb-1">客服邮箱</p>
                <p className="text-xs text-foreground-500">support@jmtt.example.com</p>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-customer-service-line text-lg"></i>
                </div>
                <p className="text-xs font-medium text-foreground-900 mb-1">在线客服</p>
                <p className="text-xs text-foreground-500">工作日 9:00-18:00</p>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                  <i className="ri-question-answer-line text-lg"></i>
                </div>
                <p className="text-xs font-medium text-foreground-900 mb-1">帮助中心</p>
                <p className="text-xs text-foreground-500">7×24小时自助服务</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}